import { images } from '@/asset';
import BoldText from '@/components/BoldText';
import ButtonAddVariable from '@/components/ButtonAddVariable';
import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import { BREAKPOINT, CartDateDisplayType, Plan, Touchpoint } from '@/constants/enum';
import { optionsCartDateDisplayType } from '@/constants/options';
import { PATH } from '@/constants/path';
import { generateDateDisplay } from '@/helpers/string';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import useGenerateEstString from '@/hooks/useGenerateEstString';
import useUserPlan from '@/hooks/useUserPlan';
import { IRuleError, IRuleErrorField } from '@/redux/slice/rule.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { Badge, Card, Image, InlineGrid, RadioButton, TextField } from '@shopify/polaris';
import { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import BoldSmallText from '../BoldSmallText';
import CustomSwitchLayout from '../CustomSwitchLayout';
import VariableTooltips from '../EstimatedTextCard/VariableTooltips';
import RegularText from '../RegularText';
import Required from '../Required';
import { EstimatedTextInCartStyled } from './styled';
import { preloadImage } from '@/helpers/file';

interface IProps {
  isNotCard?: boolean;
  title?: React.ReactNode;
  description?: string;
  showTextInCart?: boolean;
  onChangeShowTextInCart?: (value: boolean) => void;
  labelText?: string;
  onChangeLabelText: (value: string) => void;
  dateDisplay?: string;
  onChangeDateDisplay: (value: string) => void;
  orderToShipDays?: number;
  preDays?: number;
  shipToReceiveDays?: number;
  estDays?: number;
  estText?: string;
  estimatedDateDisplayType?: CartDateDisplayType;
  setEstimatedDateDisplayType?: (value: CartDateDisplayType) => void;
  hideOptionUseMessageText?: boolean;
  onFormError?: (value: { labelText: string; dateDisplay: string }) => void;
  customHandleError?: (field: IRuleErrorField, msg: string) => void;
  customError?: IRuleError[];
  preOrderDate?: string;
  isUsePreOrderDate?: boolean;
}
const EstimatedTextInCart = (props: IProps) => {
  const MAX_LENGTH_TEXT = 255;
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  const navigate = useConfirmationNavigate();
  const generateEstString = useGenerateEstString();
  const dataSettings = useSelector(dataSettingsSelector);
  const { planKey } = useUserPlan();
  const isDisabled = planKey === 'free';
  const [error, setError] = useState({ labelText: '', dateDisplay: '' });

  useEffect(() => {
    props?.onFormError?.(error);
  }, [error, props]);

  useEffect(() => {
    if (props.labelText || props.dateDisplay) {
      setError({
        ...error,
        labelText: props.labelText ? '' : error.labelText,
        dateDisplay: props.dateDisplay ? '' : error.dateDisplay,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dateDisplay, props.labelText]);

  const handleChangeError = useCallback(
    (key: keyof typeof error) => (value: string) => {
      setError({ ...error, [key]: value });
      switch (key) {
        case 'labelText':
          props.customHandleError?.('cartLabelText' as IRuleErrorField, value);
          break;
        case 'dateDisplay':
          props.customHandleError?.('cartCustomText', value);
          break;
      }
    },
    [error, props],
  );

  const handleChangeEstimatedDateDisplayType = (_: boolean, value: string) => {
    if (props.setEstimatedDateDisplayType) {
      props.setEstimatedDateDisplayType(value as CartDateDisplayType);
      if (value === CartDateDisplayType.MessageText) {
        props.customHandleError?.('cartCustomText', '');
      } else {
        if (!props.dateDisplay) {
          props.customHandleError?.('cartCustomText', 'This field is required');
        }
      }
    }
  };

  const handleAddText = useCallback(
    (value: string) => {
      if (MAX_LENGTH_TEXT - value.length - (props?.dateDisplay?.length || 0) < 0) return;
      const newValue = (props.dateDisplay ?? '') + value;
      props.onChangeDateDisplay(newValue);
      handleChangeError('dateDisplay')('');
    },
    [handleChangeError, props],
  );

  useEffect(() => {
    preloadImage(images.demo_product);
  }, []);

  const content = (
    <>
      <div className={`demo_estimated_text_cart`}>
        <Image className="demo-image" alt="" source={images.demo_product} />
        <div className="demo-content">
          <div className="demo-title">
            <div className="demo-text">Product name</div>
            <div className="demo-button">CART / CHECKOUT</div>
          </div>
          <div className="demo-text">$$</div>
          <div className="mt-16">
            {props.labelText
              ? props.labelText +
                ': ' +
                generateEstString(
                  props.estimatedDateDisplayType === CartDateDisplayType.MessageText
                    ? generateDateDisplay(props.estText || '')
                    : props.dateDisplay || '',
                  props.orderToShipDays ?? 0,
                  props.preDays ?? 1,
                  props.shipToReceiveDays ?? 2,
                  props.estDays ?? 3,
                  true,
                  '',
                  '',
                  props.preOrderDate,
                  props.isUsePreOrderDate,
                  '',
                )
              : null}
          </div>
        </div>
      </div>
      <div className={isDisabled || !props.showTextInCart ? 'disable-content' : ''}>
        <TextField
          label={
            <RegularText>
              Label text <Required />
            </RegularText>
          }
          error={error.labelText || props.customError?.find((item) => item.field === 'cartLabelText')?.msg}
          autoComplete="off"
          value={props.labelText}
          onChange={(value) => {
            props.onChangeLabelText(value);
            handleChangeError('labelText')('');
          }}
          onBlur={() => {
            if (!props.labelText) {
              handleChangeError('labelText')('This field is required');
            }
          }}
          maxLength={MAX_LENGTH_TEXT}
          showCharacterCount
          disabled={isDisabled}
        />

        <div className="mt-8">
          {isDisabled ? (
            <RegularText>
              Estimated date display <Required />
            </RegularText>
          ) : (
            <BoldSmallText>
              Estimated date display <Required />
            </BoldSmallText>
          )}
          {props.hideOptionUseMessageText ? null : (
            <InlineGrid columns={2}>
              {optionsCartDateDisplayType.map((item) => {
                return (
                  <RadioButton
                    key={item.value}
                    label={item.label}
                    checked={props.estimatedDateDisplayType === item.value}
                    id={item.value.toString()}
                    name={item.value.toString()}
                    onChange={handleChangeEstimatedDateDisplayType}
                    disabled={isDisabled}
                  />
                );
              })}
            </InlineGrid>
          )}
        </div>
        {props.estimatedDateDisplayType === CartDateDisplayType.CustomizeText || props.hideOptionUseMessageText ? (
          <>
            <RegularText>
              <VariableTooltips hideCountDown hideGeography />
            </RegularText>
            <div className="d-flex mt-8 gap-8 text-field-custom">
              <TextField
                error={error.dateDisplay || props.customError?.find((item) => item.field === 'cartCustomText')?.msg}
                label={null}
                autoComplete="off"
                value={props.dateDisplay}
                onChange={(value) => {
                  props.onChangeDateDisplay(value);
                  handleChangeError('dateDisplay')('');
                }}
                onBlur={() => {
                  if (!props.dateDisplay) {
                    handleChangeError('dateDisplay')('This field is required');
                  }
                }}
                maxLength={MAX_LENGTH_TEXT}
                showCharacterCount
                disabled={isDisabled}
              />
              <ButtonAddVariable
                handleAddText={handleAddText}
                listDisabledTemplate={{ country: true, flag: true, countDown: true, zipcode: true }}
                isAddSimpleText
                className="btn-add-variable"
                isHideBtnText={isMobile}
              />
            </div>
          </>
        ) : null}
      </div>
    </>
  );

  return (
    <EstimatedTextInCartStyled isMobile={isMobile}>
      {props.isNotCard ? (
        content
      ) : (
        <Card>
          <CustomSwitchLayout
            title={
              props.title ?? (
                <div className={`d-flex ${isMobile ? 'flex-column' : 'flex-row align-center'}`}>
                  <BoldText className={`bold-text-styled ${dataSettings.plan === Plan.FREE ? 'text--gray' : ''}`}>
                    {dataSettings?.isDynamicDateInCart
                      ? 'Show dynamic delivery estimates in Cart'
                      : 'Show static delivery estimates in Cart and Checkout'}
                  </BoldText>
                  {dataSettings.plan !== Plan.FREE && dataSettings?.isDynamicDateInCart ? (
                    <div className={`${isMobile ? 'mt-4' : 'ml-8'}`}>
                      <Badge tone="info">Beta</Badge>
                    </div>
                  ) : null}
                </div>
              )
            }
            content={
              props.description ? (
                props.description
              ) : dataSettings?.isDynamicDateInCart ? (
                <p style={{ color: '#303030' }}>
                  Refresh date based on current date, ensure accuracy if buyers abandon cart and return later. Only appear in cart
                  page or drawer of compatible themes, not in checkout.
                  {/* <span
                    onClick={() => {
                      navigate(PATH.DISPLAY_POSITION, { state: { isScrollToApplyItems: true } });
                    }}
                    style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: 4 }}
                  >
                    Change date mode
                  </span> */}
                </p>
              ) : (
                <p style={{ color: dataSettings.plan === Plan.FREE ? 'var(--p-color-text-disabled)' : '#303030' }}>
                  Date is fixed from when product is added to cart. Appear in cart, checkout, thank you, and order status page.
                  <span
                    onClick={() => {
                      navigate(PATH.DISPLAY_POSITION, { state: { isScrollToApplyItems: true } });
                    }}
                    style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: 4 }}
                  >
                    Try dynamic date mode (Beta)
                  </span>
                </p>
              )
            }
            enable={props.showTextInCart}
            onChange={(checked) => {
              if (props.onChangeShowTextInCart) {
                props.onChangeShowTextInCart(isDisabled ? false : checked);
                if (!checked) {
                  props.customHandleError?.('cartCustomText', '');
                  props.customHandleError?.('cartLabelText', '');
                } else {
                  if (!props.labelText) {
                    props.customHandleError?.('cartLabelText', 'This field is required');
                  }
                  if (!props.dateDisplay) {
                    props.customHandleError?.('cartCustomText', 'This field is required');
                  }
                }
              }
              props?.onFormError?.(error);
              setError({ labelText: '', dateDisplay: '' });
            }}
            disabled={isDisabled && !props.showTextInCart}
            rightContent={
              !isMobile && <UpgradeTextWithTouchPoint touchPoint={Touchpoint.SetupShowInCart} text="Standard or above" />
            }
          />
          {isMobile && <UpgradeTextWithTouchPoint touchPoint={Touchpoint.SetupShowInCart} text="Standard or above" />}
          <div className="mt-8">{content}</div>
        </Card>
      )}
    </EstimatedTextInCartStyled>
  );
};

export default memo(EstimatedTextInCart);
